import React, { useState, useRef, useEffect, ReactNode, cloneElement, isValidElement } from 'react';
import { createPortal } from 'react-dom';
import styles from './Tooltip.module.scss';

export type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';
export type TooltipTrigger = 'hover' | 'click' | 'focus';

export interface TooltipProps {
    /** Content to show in the tooltip */
    content: ReactNode;
    /** Position of the tooltip relative to the trigger element */
    position?: TooltipPosition;
    /** How the tooltip is triggered */
    trigger?: TooltipTrigger;
    /** Whether the tooltip is initially visible */
    isVisible?: boolean;
    /** Delay before showing tooltip (in ms) */
    showDelay?: number;
    /** Delay before hiding tooltip (in ms) */
    hideDelay?: number;
    /** Whether the tooltip should be disabled */
    disabled?: boolean;
    /** Custom class name for the tooltip */
    className?: string;
    /** The element that triggers the tooltip */
    children: ReactNode;
    /** Z-index for the tooltip portal */
    zIndex?: number;
    /** Whether to show arrow pointing to trigger */
    showArrow?: boolean;
    /** Maximum width of the tooltip */
    maxWidth?: number;
}

export const Tooltip: React.FC<TooltipProps> = ({
    content,
    position = 'top',
    trigger = 'hover',
    isVisible: controlledVisible,
    showDelay = 200,
    hideDelay = 100,
    disabled = false,
    className = '',
    children,
    zIndex = 1002,
    showArrow = true,
    maxWidth = 300,
}) => {
    const [isVisible, setIsVisible] = useState(controlledVisible || false);
    const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
    const triggerRef = useRef<HTMLElement>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);
    const showTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
    const hideTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

    // Clear timeouts on unmount
    useEffect(() => {
        return () => {
            if (showTimeoutRef.current) clearTimeout(showTimeoutRef.current);
            if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
        };
    }, []);

    // Handle controlled visibility
    useEffect(() => {
        if (controlledVisible !== undefined) {
            setIsVisible(controlledVisible);
        }
    }, [controlledVisible]);

    const calculatePosition = () => {
        if (!triggerRef.current || !tooltipRef.current) return;

        const triggerRect = triggerRef.current.getBoundingClientRect();
        const tooltipRect = tooltipRef.current;
        const scrollY = window.scrollY;
        const scrollX = window.scrollX;

        // Skip calculation if tooltip hasn't been rendered yet
        if (tooltipRect.offsetWidth === 0 || tooltipRect.offsetHeight === 0) {
            setTimeout(() => calculatePosition(), 10);
            return;
        }

        let top = 0;
        let left = 0

        switch (position) {
            case 'top':
                top = triggerRect.top + scrollY - tooltipRect.offsetHeight - 8;
                left = triggerRect.left + scrollX + (triggerRect.width - tooltipRect.offsetWidth) / 2;
                break;
            case 'bottom':
                top = triggerRect.bottom + scrollY + 8;
                left = triggerRect.left + scrollX + (triggerRect.width - tooltipRect.offsetWidth) / 2;
                break;
            case 'left':
                top = triggerRect.top + scrollY + (triggerRect.height - tooltipRect.offsetHeight) / 2;
                left = triggerRect.left + scrollX - tooltipRect.offsetWidth - 8;
                break;
            case 'right':
                top = triggerRect.top + scrollY + (triggerRect.height - tooltipRect.offsetHeight) / 2;
                left = triggerRect.right + scrollX + 8;
                break;
        }

        // Viewport boundary checks
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        // Horizontal boundary check
        if (left < 0) {
            left = 8;
        } else if (left + tooltipRect.offsetWidth > viewportWidth) {
            left = viewportWidth - tooltipRect.offsetWidth - 8;
        }

        // Vertical boundary check
        if (top < scrollY) {
            top = scrollY + 8;
        } else if (top + tooltipRect.offsetHeight > scrollY + viewportHeight) {
            top = scrollY + viewportHeight - tooltipRect.offsetHeight - 8;
        }

        setTooltipPosition({ top, left });
    };

    const showTooltip = () => {
        if (disabled) return;

        if (hideTimeoutRef.current) {
            clearTimeout(hideTimeoutRef.current);
            hideTimeoutRef.current = undefined;
        }

        if (controlledVisible === undefined) {
            showTimeoutRef.current = setTimeout(() => {
                setIsVisible(true);
            }, showDelay);
        }
    };

    const hideTooltip = () => {
        if (disabled) return;

        if (showTimeoutRef.current) {
            clearTimeout(showTimeoutRef.current);
            showTimeoutRef.current = undefined;
        }

        if (controlledVisible === undefined) {
            hideTimeoutRef.current = setTimeout(() => {
                setIsVisible(false);
            }, hideDelay);
        }
    };

    const handleMouseEnter = () => {
        if (trigger === 'hover') {
            showTooltip();
        }
    };

    const handleMouseLeave = () => {
        if (trigger === 'hover') {
            hideTooltip();
        }
    };

    const handleClick = () => {
        if (trigger === 'click') {
            if (isVisible) {
                hideTooltip();
            } else {
                showTooltip();
            }
        }
    };

    const handleFocus = () => {
        if (trigger === 'focus') {
            showTooltip();
        }
    };

    const handleBlur = () => {
        if (trigger === 'focus') {
            hideTooltip();
        }
    };

    // Update position when tooltip becomes visible
    useEffect(() => {
        if (isVisible) {
            // Use double requestAnimationFrame to ensure DOM has fully updated
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    calculatePosition();
                });
            });
        }
    }, [isVisible, position]);

    // Clone the child element and add event handlers
    const triggerElement = isValidElement(children)
        ? cloneElement(children as React.ReactElement<any>, {
              ref: triggerRef,
              onMouseEnter: handleMouseEnter,
              onMouseLeave: handleMouseLeave,
              onClick: handleClick,
              onFocus: handleFocus,
              onBlur: handleBlur,
          })
        : children;

    const tooltipContent = isVisible && content && (
        <div
            ref={tooltipRef}
            className={`${styles.tooltip} ${styles[`tooltip--${position}`]} ${className}`}
            style={{
                position: 'absolute',
                top: tooltipPosition.top || -9999,
                left: tooltipPosition.left || -9999,
                zIndex,
                maxWidth,
            }}
        >
            {showArrow && <div className={`${styles.arrow} ${styles[`arrow--${position}`]}`} />}
            <div className={styles.content}>{content}</div>
        </div>
    );

    return (
        <>
            {triggerElement}
            {typeof document !== 'undefined' && tooltipContent && createPortal(tooltipContent, document.body)}
        </>
    );
};
